import { getProgressionSkillById } from '../data/progression/skills';
import { normalizeSportName } from './drillTemplates';

function deriveDisciplineHint(trackId = '') {
  if (trackId.includes('carving')) return 'carving';
  if (trackId.includes('freeride') || trackId.includes('powder')) return 'freeride_powder';
  if (trackId.includes('freestyle')) return 'freestyle_park';
  return 'all_mountain_fundamentals';
}

function incrementCount(map, key, payload = {}) {
  if (!key) return;
  const current = map.get(key) || {
    key,
    label: payload.label || key,
    count: 0,
  };
  current.count += payload.amount || 1;
  if (payload.label) current.label = payload.label;
  map.set(key, current);
}

function toSortedList(map) {
  return Array.from(map.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function getCompletionMetrics(stages = []) {
  const totalStages = Array.isArray(stages) ? stages.length : 0;
  const completedStages = (stages || []).filter((stage) => stage?.completionStatus === 'completed').length;
  const attemptedStages = (stages || []).filter((stage) => stage?.completionStatus && stage.completionStatus !== 'not_attempted').length;

  return {
    totalStages,
    completedStages,
    attemptedStages,
    completionRatio: totalStages > 0 ? completedStages / totalStages : 0,
  };
}

function getSkillWeight(lessonInstance, metrics) {
  const outcomeStatus = lessonInstance?.lessonOutcome?.status || 'in_progress';
  const confidence = lessonInstance?.lessonOutcome?.skillConfidence || '';

  if (outcomeStatus === 'needs_review') return 0.5;
  if (confidence === 'ready_to_progress') return metrics.completionRatio >= 0.6 ? 2 : 1.5;
  if (confidence === 'solid') return metrics.completionRatio >= 0.6 ? 1.75 : 1.25;
  if (metrics.attemptedStages > 0) return 1;
  return 0.5;
}

function getNextSkillWeight(lessonInstance, metrics) {
  const outcomeStatus = lessonInstance?.lessonOutcome?.status || 'in_progress';
  const confidence = lessonInstance?.lessonOutcome?.skillConfidence || '';

  if (outcomeStatus === 'needs_review') return 0.5;
  if (confidence === 'ready_to_progress') return 2;
  if (confidence === 'solid' && metrics.completionRatio >= 0.6) return 1.5;
  return 1;
}

export function buildLessonOutcomeSignals(lessonInstances = [], { sport = '' } = {}) {
  const targetSport = normalizeSportName(sport || '');
  // Pass 77 REPAIR (§10/§11) — SPORT ISOLATION is validated against the CANONICAL target-skill sport, not only the
  // (often blank) lesson.sport string. A completed lesson belongs to the selected sport only when:
  //   · it has no internal conflict (if lesson.sport AND its target skill are both present, they must agree), AND
  //   · either its explicit lesson.sport equals the selected sport, OR (lesson.sport blank) its explicit target
  //     skill is a skill of the selected sport.
  // This blocks a blank-sport lesson carrying a cross-sport target skill from contaminating the wrong sport, while
  // still admitting sparse legacy lessons whose canonical target skill is in-sport. completedLessonCount is then the
  // count of these validated in-sport lessons (never inflated by cross-sport or self-conflicting records).
  const belongsToSport = (lesson) => {
    if ((lesson?.status || '') !== 'completed') return false;
    const lessonSport = normalizeSportName(lesson?.sport || '');
    const targetSkill = getProgressionSkillById(lesson?.targetSkillId || '');
    if (lessonSport && targetSkill && targetSkill.sport !== lessonSport) return false; // fail closed on conflict
    if (!targetSport) return true; // no selected sport (e.g. instructor pages) → count all completed
    if (lessonSport) return lessonSport === targetSport;
    return !!targetSkill && targetSkill.sport === targetSport; // blank sport: rely on canonical target-skill sport
  };
  const completedLessons = (lessonInstances || []).filter(belongsToSport);

  const reinforcedSkills = new Map();
  const nextSkillHints = new Map();
  const progressionCandidates = [];
  // Pass 77 — DEMONSTRATED skills: the TARGET skill of a completed lesson whose human outcome confidence is
  // `ready_to_progress` or `solid` (the student demonstrated that skill). This — and ONLY this — is the canonical
  // basis for a CURRENT level. `recommendedNextSkillId` (next work) and `needs_review`/low NEVER go here, and never
  // raise currentLevel. Keyed by skillId with the canonical level from skills.js.
  const demonstratedSkillsMap = new Map();

  completedLessons.forEach((lesson) => {
    const lessonSport = normalizeSportName(lesson?.sport || '');
    const targetSkill = getProgressionSkillById(lesson?.targetSkillId || '');
    // canonical sport isolation: the target skill must exist, agree with any explicit lesson.sport, and belong to the
    // selected sport (this is what stops a blank-sport lesson's cross-sport target skill producing evidence).
    if (!targetSkill || (lessonSport && targetSkill.sport !== lessonSport) || (targetSport && targetSkill.sport !== targetSport)) {
      return;
    }

    const metrics = getCompletionMetrics(lesson?.stages || []);
    const skillWeight = getSkillWeight(lesson, metrics);
    const confidence = lesson?.lessonOutcome?.skillConfidence || '';
    if (confidence === 'ready_to_progress' || confidence === 'solid') {
      demonstratedSkillsMap.set(targetSkill.skillId, {
        skillId: targetSkill.skillId,
        level: targetSkill.level,
        name: targetSkill.name,
        confidence,
      });
    }
    incrementCount(reinforcedSkills, targetSkill.skillId, {
      label: targetSkill.name,
      amount: metrics.completionRatio >= 0.6 ? 2 : 1,
    });
    progressionCandidates.push({
      movementCategory: targetSkill.category,
      skillFocus: targetSkill.name,
      disciplineHint: deriveDisciplineHint(targetSkill.trackId),
      weight: skillWeight,
      label: targetSkill.name,
      signalKey: targetSkill.skillId,
      sourceLessonId: lesson?.lessonId || '',
      type: 'lesson_completion',
    });

    const nextSkill = getProgressionSkillById(lesson?.lessonOutcome?.recommendedNextSkillId || '');
    // a cross-sport recommendedNextSkillId is ignored (must match the target skill's sport AND the selected sport)
    if (nextSkill && nextSkill.sport === targetSkill.sport && (!targetSport || nextSkill.sport === targetSport)) {
      incrementCount(nextSkillHints, nextSkill.skillId, {
        label: nextSkill.name,
        amount: 1,
      });
      progressionCandidates.push({
        movementCategory: nextSkill.category,
        skillFocus: nextSkill.name,
        disciplineHint: deriveDisciplineHint(nextSkill.trackId),
        weight: getNextSkillWeight(lesson, metrics),
        label: nextSkill.name,
        signalKey: nextSkill.skillId,
        sourceLessonId: lesson?.lessonId || '',
        type: 'lesson_next_skill_hint',
      });
    }
  });

  const reinforcedSkillList = toSortedList(reinforcedSkills);
  const nextSkillHintList = toSortedList(nextSkillHints);

  return {
    completedLessonCount: completedLessons.length,
    reinforcedSkills: reinforcedSkillList,
    nextSkillHints: nextSkillHintList,
    demonstratedSkills: Array.from(demonstratedSkillsMap.values()),
    progressionCandidates,
    summaryText:
      reinforcedSkillList.length > 0
        ? `Recent completed lessons reinforced ${reinforcedSkillList[0].label}.`
        : '',
  };
}
